import { motion } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { HERO } from "@/constants/testIds";

const IMG = "https://images.pexels.com/photos/5163935/pexels-photo-5163935.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=940";

export default function Hero() {
  const scrollContact = () => {
    document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section className="relative overflow-hidden">
      <div className="absolute inset-0 -z-10 bg-gradient-to-b from-background to-background"></div>
      <div className="absolute -z-10 top-32 -left-20 w-72 h-72 rounded-full bg-secondary/15 blur-3xl"></div>
      <div className="absolute -z-10 bottom-0 right-0 w-96 h-96 rounded-full bg-primary/10 blur-3xl"></div>

      <div className="max-w-7xl mx-auto px-6 md:px-10 pt-16 pb-24 md:pt-24 md:pb-32 grid md:grid-cols-12 gap-12 items-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="md:col-span-7"
        >
          <span className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-secondary font-medium mb-6">
            <Sparkles className="w-3.5 h-3.5" /> Trusted since 2008 · 2000+ families
          </span>
          <h1
            data-testid={HERO.heading}
            className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight text-primary"
          >
            Build &amp; Protect <br />
            <span className="italic text-secondary">Your Wealth.</span>
          </h1>
          <p className="mt-6 text-lg md:text-xl text-foreground/75 max-w-xl leading-relaxed">
            We help people manage money — but more than that, we help them manage their{" "}
            <em className="text-primary font-medium not-italic">behaviour</em>, so they can take the
            right decisions through every market cycle.
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <button
              onClick={scrollContact}
              data-testid={HERO.ctaPrimary}
              className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-full px-7 py-3.5 font-medium hover:-translate-y-0.5 transition-transform"
            >
              Talk to an Advisor <ArrowRight className="w-4 h-4" />
            </button>
            <Link
              to="/calculators"
              data-testid={HERO.ctaSecondary}
              className="inline-flex items-center gap-2 border border-primary/30 text-primary rounded-full px-7 py-3.5 font-medium hover:bg-primary/5 transition-colors"
            >
              Try a Calculator
            </Link>
          </div>

          <div className="mt-12 flex items-center gap-8">
            <div>
              <div className="font-serif text-3xl text-primary">17+</div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground">Years guiding</div>
            </div>
            <div className="w-px h-10 bg-border"></div>
            <div>
              <div className="font-serif text-3xl text-primary">2000+</div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground">Families served</div>
            </div>
            <div className="w-px h-10 bg-border"></div>
            <div>
              <div className="font-serif text-3xl text-primary">3</div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground">Expert advisors</div>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.9, delay: 0.2, ease: "easeOut" }}
          className="md:col-span-5 relative"
        >
          <div className="relative aspect-[4/5] rounded-[2rem] overflow-hidden grain shadow-xl">
            <img
              src={IMG}
              alt="A family planning their financial future together"
              className="w-full h-full object-cover"
            />
          </div>
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="absolute -bottom-8 -left-6 md:-left-12 bg-card rounded-2xl p-5 shadow-lg border border-border max-w-[240px]"
          >
            <p className="text-xs uppercase tracking-wider text-muted-foreground">Goal achieved</p>
            <p className="font-serif text-2xl text-primary mt-1">Retirement at 58</p>
            <p className="text-xs text-foreground/60 mt-1">Mehta family · SIP since 2014</p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
